import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, MessageSquare, Trash2, Calendar } from 'lucide-react';
import { getUserSessions, deleteSession } from '../../../../../services/chatService';
import * as agentService from '../../../../../services/agentService';
import { Session } from '../../../../../types/chat';
import { Agent } from '../../../../../types/agent';
import { LoadingSpinner } from '../../../../common';
import { AdminEmptyState } from '../../../ui';
// ChatPopup still needs migration - using original for now
import ChatPopup from '../../../../agent/components/ChatPopup';

interface SessionCardProps {
  session: Session;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

const SessionCard: React.FC<SessionCardProps> = ({ session, onClick, onDelete }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div
      onClick={onClick}
      className="
        bg-[#1a1a1a] rounded-xl p-4
        border border-[#333333] hover:border-[#F4CC04]
        cursor-pointer
        transition-all duration-200
      "
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-medium text-sm line-clamp-1">
            {session.chat_title || 'Chat sem título'}
          </h3>
          <div className="flex items-center gap-1 mt-2 text-xs text-[#888888]">
            <Calendar className="w-3 h-3" />
            {formatDate(session.created_at)}
          </div>
        </div>
        <button
          onClick={onDelete}
          className="p-1.5 text-[#888888] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          title="Excluir sessão"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#333333]">
        <MessageSquare className="w-3 h-3 text-[#888888]" />
        <span className="text-xs text-[#888888]">
          {session.messages_count || 0} mensagen{(session.messages_count || 0) !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export const AgentTest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showChatPopup, setShowChatPopup] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [agent, setAgent] = useState<Agent | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) return;

        const agentResponse = await agentService.getAgent(parseInt(id));
        if (agentResponse.success && agentResponse.data) {
          setAgent(agentResponse.data);
        }

        const sessionsResponse = await getUserSessions();
        if (sessionsResponse.success) {
          const currentAgentId = parseInt(id);
          const agentSessions = sessionsResponse.data.filter(
            (session: Session) => session.agent_id != null && session.agent_id === currentAgentId
          );
          setSessions(agentSessions);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao carregar dados');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleNewChat = () => {
    setSelectedSessionId(null);
    setShowChatPopup(true);
  };

  const handleOpenSession = (sessionId: number) => {
    setSelectedSessionId(sessionId);
    setShowChatPopup(true);
  };

  const handleDeleteSession = async (sessionId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await deleteSession(sessionId);
      if (response.success) {
        setSessions((prev) => prev.filter((session) => session.id !== sessionId));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao excluir sessão');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Sessões de Teste</h2>
        <button
          onClick={handleNewChat}
          className="
            flex items-center gap-2 px-3 py-2
            bg-[#F4CC04] hover:bg-[#D4B104]
            text-black text-sm font-medium
            rounded-lg transition-colors
          "
        >
          <Plus className="w-4 h-4" />
          Novo Chat
        </button>
      </div>

      {/* Sessions */}
      {sessions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              onClick={() => handleOpenSession(session.id)}
              onDelete={(e) => handleDeleteSession(session.id, e)}
            />
          ))}
        </div>
      ) : (
        <AdminEmptyState
          title="Nenhuma Sessão de Chat"
          description="Inicie um novo chat para testar este agente"
          action={
            <button
              onClick={handleNewChat}
              className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
            >
              Novo Chat
            </button>
          }
        />
      )}

      {showChatPopup && (
        <ChatPopup
          agentId={parseInt(id!)}
          sessionId={selectedSessionId}
          agent={agent}
          onClose={() => {
            setShowChatPopup(false);
            setSelectedSessionId(null);
          }}
          onSessionCreated={(newSession) => {
            setSessions((prev) => [newSession, ...prev]);
          }}
        />
      )}
    </div>
  );
};

export default AgentTest;
